import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, X, Paperclip } from "lucide-react";
import api, { formatApiError } from "./api";

function quoteText(msg) {
    if (!msg) return "";
    const dateLine = msg.date ? new Date(msg.date).toLocaleString() : "";
    const intro = `\n\nOn ${dateLine}, ${msg.from} wrote:\n`;
    const body = (msg.text || stripHtml(msg.html) || "").split("\n").map((l) => `> ${l}`).join("\n");
    return intro + body;
}
function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export default function PortalCompose() {
    const location = useLocation();
    const navigate = useNavigate();
    const reply = location.state?.reply;

    const initial = useMemo(() => {
        if (!reply)
            return {
                to: "",
                cc: "",
                subject: "",
                body: "",
                in_reply_to: "",
                references: "",
            };
        const subj = reply.subject?.startsWith("Re:")
            ? reply.subject
            : `Re: ${reply.subject || ""}`.trim();
        return {
            to: reply.from || "",
            cc: "",
            subject: subj,
            body: quoteText(reply),
            in_reply_to: reply.message_id || "",
            references: reply.message_id || "",
        };
    }, [reply]);

    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setForm(initial);
    }, [initial]);

    const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const valid =
        form.to.trim().length > 0 &&
        /\S+@\S+\.\S+/.test(form.to.split(",")[0].trim()) &&
        form.subject.trim().length > 0;

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || loading) return;
        setLoading(true);
        try {
            const payload = {
                to: form.to
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                cc: form.cc
                    ? form.cc
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                    : null,
                subject: form.subject,
                body_text: form.body,
                in_reply_to: form.in_reply_to || null,
                references: form.references || null,
            };
            await api.post("/portal/send", payload);
            toast.success("Email sent", {
                description: `Delivered to ${payload.to.join(", ")}`,
            });
            navigate("/portal");
        } catch (e) {
            toast.error("Send failed", {
                description: formatApiError(e?.response?.data?.detail) || e.message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pr-glass-strong rounded-2xl overflow-hidden" data-testid="compose-page">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <h2 className="font-outfit text-lg font-semibold text-white">
                    {reply ? "Reply" : "New message"}
                </h2>
                <button
                    onClick={() => navigate("/portal")}
                    className="w-8 h-8 rounded-lg pr-glass flex items-center justify-center text-slate-300 hover:text-white"
                    data-testid="compose-close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4" data-testid="compose-form">
                <Row label="To">
                    <input
                        type="text"
                        value={form.to}
                        onChange={handle("to")}
                        placeholder="someone@example.com, another@example.com"
                        required
                        className="input-glow w-full rounded-xl py-2.5 px-4 text-sm text-white placeholder:text-slate-500"
                        data-testid="compose-to"
                    />
                </Row>
                <Row label="Cc">
                    <input
                        type="text"
                        value={form.cc}
                        onChange={handle("cc")}
                        placeholder="optional"
                        className="input-glow w-full rounded-xl py-2.5 px-4 text-sm text-white placeholder:text-slate-500"
                        data-testid="compose-cc"
                    />
                </Row>
                <Row label="Subject">
                    <input
                        type="text"
                        value={form.subject}
                        onChange={handle("subject")}
                        required
                        className="input-glow w-full rounded-xl py-2.5 px-4 text-sm text-white placeholder:text-slate-500"
                        data-testid="compose-subject"
                    />
                </Row>

                <div>
                    <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                        Message
                    </label>
                    <textarea
                        value={form.body}
                        onChange={handle("body")}
                        rows={14}
                        placeholder="Write your message…"
                        className="input-glow w-full rounded-xl py-3 px-4 text-sm text-white placeholder:text-slate-500 font-inter leading-relaxed resize-vertical"
                        data-testid="compose-body"
                    />
                    <p className="mt-2 text-[11px] font-mono-pr text-slate-500 flex items-center gap-2">
                        <Paperclip className="w-3 h-3" />
                        Attachments coming soon — text & HTML send for now.
                    </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                    <button
                        type="submit"
                        disabled={!valid || loading}
                        className="btn-primary disabled:opacity-50"
                        data-testid="compose-send"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        {reply ? "Send reply" : "Send"}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/portal")}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="grid grid-cols-[80px_1fr] items-center gap-3">
            <label className="font-outfit text-xs uppercase tracking-[0.22em] text-blue-300">
                {label}
            </label>
            {children}
        </div>
    );
}
